import {
    Button,
    ExclusiveChoice,
    ExclusiveChoiceDropdown,
    ModalPanel,
    Title
} from "dash-styled"
import { FormEvent, ReactNode, useCallback, useMemo, useState } from "react"
import { Labeled } from "../../../../../../Common/Render/Common/Labeled/Labeled"
import {
    AppWorkerRole,
    AppWorkerRoles,
    toDisplayText
} from "../../../../../../../Models/API/AppWorkerRole"
import { ReactComponent as IconClose } from "icons/lib/close-icon.svg"
import { ReactComponent as IconReload } from "icons/lib/reload.svg"
import { FlatButton } from "../../../../../../Common/FlatButton/FlatButton"

interface AssignModalProps<T> {
    isSaving: boolean
    resource: "worker" | "worker group"
    models: T[]
    isAlreadyAssigned: (model: T) => boolean
    getId: (model: T) => number
    renderModel: (model: T) => string
    createLink: ReactNode
    onClose: () => void
    onSubmit: (ids: number[], role: AppWorkerRole) => void
    onLoadMore: () => void
    onReload: () => void
}

export function AssignModal<T>({
    isSaving,
    resource,
    models,
    isAlreadyAssigned,
    renderModel,
    createLink,
    getId,
    onClose,
    onSubmit,
    onLoadMore,
    onReload
}: AssignModalProps<T>) {
    const [isReloading, setReloading] = useState(false)
    const [role, setRole] = useState<AppWorkerRole>()
    const [selected, setSelected] = useState<T[]>([])
    const handleSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault()

            if (role !== undefined && selected.length > 0) {
                onSubmit(selected.map(getId), role)
            }
        },
        [getId, onSubmit, role, selected]
    )
    const handleAdd = useCallback(
        (model: T) => setSelected(selected.concat([model])),
        [selected]
    )
    const modelsToAdd = useMemo(
        () =>
            models.filter(
                model =>
                    !isAlreadyAssigned(model) &&
                    selected.every(s => getId(s) !== getId(model))
            ),
        [getId, isAlreadyAssigned, selected, models]
    )
    const handleReload = useCallback(async () => {
        try {
            setReloading(true)
            await onReload()
        } finally {
            setReloading(false)
        }
    }, [onReload])

    const saveButtonLabel = isSaving
        ? "Adding..."
        : selected.length <= 1
        ? `Add a ${resource}`
        : `Add ${selected.length} ${
              resource === "worker" ? "workers" : "worker groups"
          }`

    return (
        <ModalPanel onClose={onClose} className="max-w-2xl w-full">
            <Title variant="heading2" weight="semibold" component="h2">
                Add {resource} to project
            </Title>
            <form onSubmit={handleSubmit}>
                <div className="flex items-end">
                    <ExclusiveChoiceDropdown
                        className="flex-grow mr-2"
                        label={
                            resource === "worker" ? "Workers" : "Worker groups"
                        }
                        placeholder={`Select ${resource} to add`}
                        choices={modelsToAdd}
                        value={undefined}
                        renderChoice={renderModel}
                        onClick={handleAdd}
                        onReachedToEndOfChoices={onLoadMore}
                    />
                    <FlatButton
                        size="sm"
                        className="px-4"
                        onClick={handleReload}
                    >
                        <IconReload
                            className={`inline w-4 h-4 mr-1 ${
                                isReloading ? "animate-spin" : ""
                            }`}
                        />
                        Refresh
                    </FlatButton>
                </div>
                {selected.length > 0 && (
                    <ul className="max-h-40vh overflow-auto mt-2 border-0 border-t border-b border-solid border-rhino-300 divide-y divide-solid divide-rhino-300">
                        {selected.map(model => (
                            <li
                                key={getId(model)}
                                className="py-3 px-2 flex items-center justify-between"
                            >
                                {renderModel(model)}
                                <button
                                    type="button"
                                    className="text-smoke-350 align-middle"
                                    onClick={() =>
                                        setSelected(current =>
                                            current.filter(
                                                selectedModel =>
                                                    getId(selectedModel) !==
                                                    getId(model)
                                            )
                                        )
                                    }
                                >
                                    <IconClose className="w-4 h-4" />
                                    <span className="sr-only">Delete</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                {createLink}
                <Labeled label="Role" errors={[]}>
                    <ExclusiveChoice
                        withPanel={false}
                        choices={[...AppWorkerRoles]}
                        selectedIndex={
                            role === undefined
                                ? -1
                                : AppWorkerRoles.indexOf(role)
                        }
                        renderChoice={toDisplayText}
                        onClick={index => setRole(AppWorkerRoles[index])}
                    />
                </Labeled>
                <Button
                    type="submit"
                    className="w-auto mt-4 px-8"
                    disabled={
                        isSaving || role === undefined || selected.length < 1
                    }
                >
                    {saveButtonLabel}
                </Button>
            </form>
        </ModalPanel>
    )
}
